import React, { useEffect, useMemo, useState } from 'react';
import './AdminCliCommandsPage.scss';
import { InputText } from 'primereact/inputtext';
import type { AdminCliCommand, AdminCliCommandOption } from '@brand24-original/llm-types';
import Loader from '../../../shared/components/common/loader/Loader';
import EmptyPanel from '../../../shared/components/empty-panel/EmptyPanel';
import StatTile from '../../../shared/components/stat-tile/StatTile';
import Sparkles from '../../../shared/components/sparkles/Sparkles';
import { getCliCommands } from '../../../shared/services/admin/cli-commands/AdminCliCommandsService';
import { copyTextToClipboard } from '../../../shared/utils/utils';

// Fixed runbook, one card per step — the command cards below come from the worker.
const HOW_TO: { title: string; icon: string; text: React.ReactNode }[] = [
  {
    title: 'Where',
    icon: 'ico--globe',
    text: (
      <>
        On <strong>llm-worker</strong>, not the backend: commands need the worker&apos;s <code>.env</code>{' '}
        (database, Redis, provider keys). Open a shell on the worker host or inside its container.
      </>
    ),
  },
  {
    title: 'Run',
    icon: 'ico--run',
    text: (
      <>
        From the repo: <code>cd apps/llm-worker</code>, then <code>pnpm cli &lt;command&gt; [options]</code>{' '}
        (TypeScript sources). On a built image: <code>pnpm cli:prod &lt;command&gt;</code> runs{' '}
        <code>dist/cli.js</code> after <code>pnpm build</code>.
      </>
    ),
  },
  {
    title: 'Options',
    icon: 'ico--settings',
    text: (
      <>
        <code>pnpm cli &lt;command&gt; --help</code> prints the same options as the cards. Flags marked{' '}
        <em>required</em> must be given; the rest fall back to the default shown.
      </>
    ),
  },
  {
    title: 'Cost & safety',
    icon: 'ico--shield-check',
    text: (
      <>
        LLM and provider calls cost money and some commands rewrite data. Prefer <code>--dry-run</code>{' '}
        where it exists, start with a small <code>--limit</code>, read the description for what is written.
      </>
    ),
  },
  {
    title: 'Long runs',
    icon: 'ico--refresh',
    text: (
      <>
        Start them under <code>nohup</code> or <code>screen</code> so a dropped SSH session does not kill
        them. Resumable commands say so in their description.
      </>
    ),
  },
  {
    title: 'Source',
    icon: 'ico--info',
    text: (
      <>
        This list is read live from the deployed worker&apos;s <code>src/cli.ts</code>, so it always matches
        the running version. Adding a command there is enough to show it here.
      </>
    ),
  },
];

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  // Shared helper: `navigator.clipboard` exists only on HTTPS/localhost, the helper falls back to execCommand.
  const copy = () => {
    void copyTextToClipboard(text).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button type="button" className="cli-card__copy" onClick={copy}>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const OptionRow: React.FC<{ option: AdminCliCommandOption }> = ({ option }) => (
  <div className="cli-card__row">
    <div className="cli-card__row-head">
      <span className="cli-card__key">{option.flags}</span>
      <span className="cli-card__pills">
        {option.mandatory && <span className="cli-card__pill cli-card__pill--required">required</span>}
        {option.defaultValue !== null && (
          <span className="cli-card__pill cli-card__pill--default" title="Default">
            {option.defaultValue}
          </span>
        )}
        {option.choices && (
          <span className="cli-card__pill cli-card__pill--choices" title="Allowed values">
            {option.choices.join(' | ')}
          </span>
        )}
      </span>
    </div>
    {option.description && <span className="cli-card__row-desc">{option.description}</span>}
  </div>
);

const CommandCard: React.FC<{ command: AdminCliCommand }> = ({ command }) => (
  <div className="cli-card" id={command.name}>
    <Sparkles icon="ico--run" />
    <span className="cli-card__name">{command.name}</span>
    <p className="cli-card__text">{command.description}</p>
    <div className="cli-card__usage">
      <span>{command.usage}</span>
      <CopyButton text={command.usage} />
    </div>
    {command.options.length > 0 && (
      <div className="cli-card__rows">
        {command.options.map((option) => (
          <OptionRow key={option.flags} option={option} />
        ))}
      </div>
    )}
  </div>
);

/**
 * The llm-worker CLI as a runbook in the admin dashboard's style: counters, a "How to run" card row and one
 * card per command, read live from the deployed worker. Nothing here runs anything.
 */
const AdminCliCommandsPage: React.FC = () => {
  const [commands, setCommands] = useState<AdminCliCommand[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    getCliCommands()
      .then(setCommands)
      .catch(() => {
        setError('Could not load the command list from the worker.');
        setCommands([]);
      });
  }, []);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!commands) return [];
    if (!needle) return commands;
    return commands.filter(
      (c) =>
        c.name.includes(needle) ||
        c.description.toLowerCase().includes(needle) ||
        c.options.some((o) => o.flags.includes(needle))
    );
  }, [commands, filter]);

  const counters = commands
    ? [
        { title: 'Commands', value: commands.length, icon: 'ico--run' },
        {
          title: 'With --dry-run',
          value: commands.filter((c) => c.options.some((o) => o.flags.startsWith('--dry-run'))).length,
          icon: 'ico--shield-check',
        },
        {
          title: 'Need no options',
          value: commands.filter((c) => !c.options.some((o) => o.mandatory)).length,
          icon: 'ico--zap',
        },
      ]
    : [];

  return (
    <div className="admin-cli">
      <div className={`stat-tiles admin-cli__section${commands === null ? ' admin-cli__section--loading' : ''}`}>
        {commands === null && <Loader />}
        {counters.map((counter) => (
          <StatTile key={counter.title} title={counter.title} value={counter.value} icon={counter.icon} />
        ))}
      </div>

      <div className="page-box__title">How to run a command</div>
      <div className="admin-cli__howto">
        {HOW_TO.map((step) => (
          <div key={step.title} className="cli-card">
            <Sparkles icon={step.icon} />
            <div className="cli-card__title">{step.title}</div>
            <p className="cli-card__text">{step.text}</p>
          </div>
        ))}
      </div>

      <div className="page-box__title">
        Commands
        <span className="admin-cli__filter">
          <InputText
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name, description or flag"
          />
        </span>
      </div>
      <div className={`admin-cli__section${commands === null ? ' admin-cli__section--loading' : ''}`}>
        {commands === null && <Loader />}
        {commands !== null && visible.length === 0 && (
          <div className="admin-cli__empty">
            <EmptyPanel
              size="xs"
              textKey={error ?? 'No command matches the filter'}
              descriptionKey={
                error
                  ? 'The list comes from the running llm-worker. Check that it is up and that INTERNAL_URL points at it.'
                  : ''
              }
            />
          </div>
        )}
        {visible.length > 0 && (
          <div className="admin-cli__list">
            {visible.map((command) => (
              <CommandCard key={command.name} command={command} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCliCommandsPage;
